import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Download, MessageCircle } from "lucide-react";
import { PRODUCT_BY_SLUG, BRAND, PRODUCTS } from "../data/site";
import { useSEO } from "../lib/seo";
import LeadForm from "../components/LeadForm";
import SafeImage from "../components/SafeImage";
import { useCatalog } from "../context/CatalogContext";

export default function ProductDetail() {
    const { slug } = useParams();
    const p = PRODUCT_BY_SLUG[slug];
    const { open } = useCatalog();

    useSEO({
        title: p ? `${p.h1} | RP Traders, Guntur` : "Product | RP Traders",
        description: p
            ? `${p.blurb} Grades: ${p.grades.join(", ")}. Bulk supply to ${p.markets.join(", ")} and more. Request a quote within 24 hours.`
            : "",
        keywords: p?.keywords || "Indian spices exporter",
        ogImage: p?.image,
    });

    if (!p) return <Navigate to="/products" replace />;

    const others = PRODUCTS.filter((x) => x.slug !== p.slug).slice(0, 3);

    return (
        <main className="bg-white pt-28 sm:pt-32">
            {/* Hero */}
            <section className="relative isolate overflow-hidden">
                <div className="absolute inset-0 -z-10">
                    <SafeImage src={p.image} alt={p.name} eager className="" />
                    <div className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/55 to-black/85" />
                </div>
                <div className="mx-auto max-w-7xl px-5 py-20 text-white sm:px-8 sm:py-28 lg:px-10">
                    <Link
                        to="/products"
                        data-testid="pd-back"
                        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/80 hover:text-white"
                    >
                        <ArrowLeft className="h-3.5 w-3.5" /> All products
                    </Link>
                    <span className="mt-6 inline-flex items-center rounded-full border border-white/25 bg-white/10 px-3 py-1 text-[11px] font-medium uppercase tracking-[0.22em] text-white/90 backdrop-blur">
                        {p.tag}
                    </span>
                    <h1
                        data-testid="pd-title"
                        className="mt-5 max-w-4xl font-display text-4xl font-bold leading-[1.05] tracking-[-0.03em] sm:text-6xl"
                    >
                        {p.h1}
                    </h1>
                    <p className="mt-5 max-w-3xl text-base leading-relaxed text-white/85 sm:text-lg">
                        {p.blurb}
                    </p>
                    <div className="mt-8 flex flex-wrap gap-3">
                        <a
                            href="#quote"
                            data-testid="pd-cta-quote"
                            className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3.5 text-sm font-semibold text-ink hover:scale-[1.03] transition-transform"
                        >
                            Request a Quote <ArrowRight className="h-4 w-4" />
                        </a>
                        <button
                            type="button"
                            onClick={open}
                            data-testid="pd-cta-catalog"
                            className="inline-flex items-center gap-2 rounded-full bg-brand-red px-6 py-3.5 text-sm font-semibold text-white hover:scale-[1.03] transition-transform"
                        >
                            <Download className="h-4 w-4" /> Download Catalog
                        </button>
                        <a
                            href={`https://wa.me/${BRAND.leadWhatsapp}?text=${encodeURIComponent(
                                `Hello RP Traders, I'd like a quote for ${p.name}.`
                            )}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="pd-cta-wa"
                            className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-6 py-3.5 text-sm font-semibold text-white hover:scale-[1.03] transition-transform"
                        >
                            <MessageCircle className="h-4 w-4" /> WhatsApp
                        </a>
                    </div>
                </div>
            </section>

            {/* Specs & Highlights */}
            <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-24 lg:px-10">
                <div className="grid gap-12 lg:grid-cols-12">
                    <div className="lg:col-span-7">
                        <span className="text-xs uppercase tracking-[0.22em] text-brand-red">Product Overview</span>
                        <h2 className="mt-3 font-display text-3xl font-bold tracking-[-0.025em] sm:text-4xl">
                            Quality you can ship with.
                        </h2>
                        <p className="mt-5 text-ink-muted">
                            RP Traders sources {p.name.toLowerCase()} from established growing regions
                            in India and processes every consignment to international export
                            standards. Documentation including Certificate of Origin, Phytosanitary
                            and Fumigation is provided with every shipment.
                        </p>

                        <h3 className="mt-10 font-display text-lg font-bold">Available Grades</h3>
                        <div className="mt-3 flex flex-wrap gap-2">
                            {p.grades.map((g) => (
                                <span
                                    key={g}
                                    data-testid={`pd-grade-${g.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                    className="rounded-full border border-hairline bg-white px-4 py-2 text-sm font-medium text-ink"
                                >
                                    {g}
                                </span>
                            ))}
                        </div>

                        <h3 className="mt-10 font-display text-lg font-bold">Packaging Options</h3>
                        <ul className="mt-3 space-y-2">
                            {p.packaging.map((pk) => (
                                <li key={pk} className="flex items-start gap-2 text-sm text-ink-muted">
                                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-red" />
                                    {pk}
                                </li>
                            ))}
                        </ul>

                        <h3 className="mt-10 font-display text-lg font-bold">Export Markets</h3>
                        <div className="mt-3 flex flex-wrap gap-2">
                            {p.markets.map((m) => (
                                <span key={m} className="rounded-full bg-brand-turmeric/15 px-3 py-1 text-xs font-medium text-brand-turmericDeep">
                                    {m}
                                </span>
                            ))}
                        </div>
                    </div>

                    <div className="lg:col-span-5">
                        <div className="sticky top-32 rounded-3xl border border-hairline bg-neutral-bg p-6 sm:p-8">
                            <span className="text-xs uppercase tracking-[0.22em] text-brand-red">
                                Technical Specs
                            </span>
                            <h3 className="mt-2 font-display text-2xl font-bold tracking-tight">
                                {p.name}
                            </h3>
                            <dl className="mt-5 grid gap-px overflow-hidden rounded-2xl border border-hairline bg-hairline">
                                {p.specs.map((s) => (
                                    <div key={s.k} className="grid grid-cols-2 gap-3 bg-white p-4 text-sm">
                                        <dt className="text-ink-muted">{s.k}</dt>
                                        <dd className="font-medium text-ink">{s.v}</dd>
                                    </div>
                                ))}
                            </dl>
                            <button
                                onClick={open}
                                data-testid="pd-side-catalog"
                                className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full border border-hairline bg-white px-5 py-3 text-sm font-semibold text-ink hover:bg-black/5"
                            >
                                <Download className="h-4 w-4" /> Download full spec sheet
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Quote form */}
            <section id="quote" data-testid="pd-quote" className="bg-neutral-bg py-24 sm:py-28">
                <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-10">
                    <div className="grid gap-10 lg:grid-cols-12">
                        <div className="lg:col-span-5">
                            <span className="text-xs uppercase tracking-[0.22em] text-brand-red">
                                Bulk Order Enquiry
                            </span>
                            <h2 className="mt-3 font-display text-3xl font-bold tracking-[-0.025em] sm:text-5xl">
                                Request a quote for {p.name}.
                            </h2>
                            <p className="mt-4 text-ink-muted">
                                Share your country, target quantity and any specific grade. We
                                respond within 24 hours with pricing, samples and shipping options.
                            </p>
                        </div>
                        <div className="lg:col-span-7">
                            <LeadForm defaultProduct={p.id} source={`product:${p.slug}`} />
                        </div>
                    </div>
                </div>
            </section>

            {/* Other products */}
            <section className="bg-white py-20">
                <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-10">
                    <div className="flex items-end justify-between gap-6">
                        <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">
                            Other products
                        </h2>
                        <Link to="/products" className="text-sm font-semibold text-brand-red hover:underline">
                            View all →
                        </Link>
                    </div>
                    <div className="mt-8 grid gap-6 md:grid-cols-3">
                        {others.map((o) => (
                            <Link
                                key={o.id}
                                to={`/products/${o.slug}`}
                                className="group overflow-hidden rounded-3xl border border-hairline bg-white"
                            >
                                <div className="aspect-[4/3] overflow-hidden bg-black/5">
                                    <SafeImage src={o.image} alt={o.name} className="transition-transform duration-700 group-hover:scale-[1.06]" />
                                </div>
                                <div className="p-5">
                                    <h3 className="font-display text-base font-bold">{o.name}</h3>
                                    <p className="mt-1 line-clamp-2 text-sm text-ink-muted">{o.blurb}</p>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>
        </main>
    );
}
